import { format } from "date-fns";
import { MoreHorizontal, Pencil, Check, X, User, Briefcase, FileText, Link2, CreditCard } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { CouplesPack } from "@/pages/couples-pack/types/couples-pack";
import { Input } from "@/components/ui/input";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProfessionalPicker } from "./professional-picker";
import { getIndividialSessionSchedulingLink } from "../services/get-individual-session-scheduling-link";
import { updateCouplesPackProfessional } from "../services/update-couples-pack-professional";
import { useUpdateCouplesPackProfessional } from "../hooks/use-update-couples-pack-professional";
import { getJointSessionSchedulingLink } from "../services/get-joint-session-scheduling-link";
import { useCancelSession } from "@/features/sessions/hooks/use-cancel-session";
import { updateCouplesPackUsers } from "../services/update-couples-pack-users";
import { useUpdateCouplesPackUsers } from "../hooks/use-update-couples-pack-users";
import { CANCELLATION_REASONS } from "@/utils/cancellation-reasons";
import { useCreateCouplesDocument } from "../hooks/use-create-couples-document";

interface CouplesPackCardProps {
  couplesPack: CouplesPack;
  onReschedule?: (id: string) => void;
  // Opens the shared UserDetailsDrawer, which the page owns — same wiring as
  // SessionCard, so one drawer instance serves the whole list.
  onUserClick?: (userId: string) => void;
}

export function CouplesPackCard({ couplesPack, onReschedule, onUserClick }: CouplesPackCardProps) {
  const canWrite = useHasAccess("write");
  const {
    id,
    status,
    start_date,
    end_date,
    total_session_quantity,
    utilized_session_quantity,
    extra_reschedule_session,
    extra_exception_session,
    sessions,
    users,
    professional_id,
    resolved_purchaser,
    pending_partner
  } = couplesPack;

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>("");
  const [cancelReason, setCancelReason] = useState<string | undefined>(undefined);
  const { mutate: cancelSession } = useCancelSession();

  const isOthersSelected = selectedCancelReason === "Others (type reason manually if not listed)";

  const { data: professionalsData, isLoading: professionalsLoading } = useAllProfessionals();
  const professionals = professionalsData?.professionals || [];

  const [selectedProfessional, setSelectedProfessional] = useState<string | null>(
    professional_id || null
  );

  const [newUserIds, setNewUserIds] = useState<string[]>([]);

  // Ensure array length matches empty slots
  const missingUsers = 2 - (users?.length || 0);
  useEffect(() => {
    setNewUserIds(Array(missingUsers).fill(""));
  }, [users]);

  const { mutate: updateProfessional } = useUpdateCouplesPackProfessional();
  const { mutate: updateCouplesPackUsers } = useUpdateCouplesPackUsers();
  const { mutate: createCouplesDocument } = useCreateCouplesDocument();

  const handleAssignProfessional = (professional_id: string) => {
  
    updateProfessional(
        { professional_id, couples_pack_usage_identifier: couplesPack.id },
        {
          onSuccess: () => {
            toast({ title: "Updated usage identifier" });
            setSelectedProfessional(professional_id);
          },
          onError: () => {
            toast({
              title: "Failed to update usage identifier",
              variant: "destructive",
            });
          },
        }
      );
  };

  const handleUpdateUsers = (userIds: string[]) => {
  
    updateCouplesPackUsers(
        { couples_pack_usage_identifier: couplesPack.id, user_ids: userIds,},
        {
          onSuccess: () => {
            toast({ title: "Updated usage identifier" });
          },
          onError: () => {
            toast({
              title: "Failed to update usage identifier",
              variant: "destructive",
            });
          },
        }
      );
  };

  return (
    <Card className="w-full">
      {/* Header */}
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-secondary-background">
        {/* Left side: Title + dates */}
        <div>
            <div className="flex items-center gap-2">
            <CardTitle className="text-xl font-bold text-sidebar-primary">
                Couples Pack
            </CardTitle>
            <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={async () => {
                try {
                    await navigator.clipboard.writeText(id);
                    toast({
                    title: "Copied",
                    description: "Couples Pack ID copied",
                    });
                } catch {
                    toast({
                    title: "Error",
                    description: "Failed to copy Couples Pack ID",
                    variant: "destructive",
                    });
                }
                }}
            >
                <Copy className="h-4 w-4" />
            </Button>
            </div>

            {/* Dates */}
            <p className="text-sm text-muted-foreground mt-1">
            {format(new Date(start_date), "MMM d, yyyy")} →{" "}
            {format(new Date(end_date), "MMM d, yyyy")}
            </p>
        </div>

        {/* Right side: Status */}
        <div>
            <Badge
            variant={
                status.toLowerCase() === "active"
                ? "active"
                : status.toLowerCase() === "canceled"
                ? "destructive"
                : "secondary"
            }
            >
            {status.toUpperCase()}
            </Badge>
        </div>
        </CardHeader>



      {/* Content */}
      <CardContent className="bg-secondary-background space-y-4">
        {/* Session info */}
        <div className="flex justify-between">
          <div>
            <p className="text-sm">
              Total Sessions:{" "}
              <span className="font-medium">{total_session_quantity}</span>
            </p>
            <p className="text-sm">
              Utilized:{" "}
              <span className="font-medium">{utilized_session_quantity}</span>
            </p>
          </div>
          <div>
            <p className="text-sm">
              Extra Reschedules:{" "}
              <span className="font-medium">{extra_reschedule_session}</span>
            </p>
            <p className="text-sm">
              Extra Exceptions:{" "}
              <span className="font-medium">{extra_exception_session}</span>
            </p>
          </div>
        </div>

        {/* Sessions */}
        {sessions && sessions.length > 0 && (
        <div>
            <p className="text-sm font-medium mb-1">Sessions:</p>
            <div className="space-y-2 overflow-y-auto rounded-md border bg-white p-2">
            {sessions.map((session) => (
                <div
                key={session.id}
                className="flex items-center justify-between rounded-md border p-2 bg-secondary"
                >
                {/* Left content */}
                <div className="space-y-1">
                    {/* Session name */}
                    <p className="text-sm font-semibold">{session.name}</p>

                    {/* Invitee */}
                    {session.invitee_full_name && (
                    <p className="text-xs text-muted-foreground">
                        Invitee: {session.invitee_full_name}
                    </p>
                    )}

                    {/* Date */}
                    <p className="text-xs text-muted-foreground">
                    {format(new Date(session.start_time), "MMMM d, yyyy h:mm a")}
                    </p>
                </div>

                {/* Right content */}
                <div className="flex items-center gap-2">
                    {/* Status badge */}
                    <Badge
                    variant={
                        session.status === "active"
                        ? "active"
                        : session.status === "canceled"
                        ? "destructive"
                        : "secondary"
                    }
                    >
                    {session.status.toUpperCase()}
                    </Badge>

                    {/* Actions if active */}
                    {session.status === "active" && (
                    <>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                            <HasAccess permission="write">
                            {onReschedule && (
                                <DropdownMenuItem onClick={() => onReschedule(session.id)}>
                                Reschedule
                                </DropdownMenuItem>
                            )}
                            </HasAccess>
                            <DropdownMenuItem
                                onClick={
                                    async () => {
                                        try {
                                            if (session.source_id) {
                                                const lowerName = (session.name || "").toLowerCase();
                                                let sessionType: "individual" | "joint" = "individual";

                                                if (
                                                    lowerName.includes("joint") ||
                                                    lowerName.includes("couple")
                                                ) {
                                                    sessionType = "joint";
                                                } else if (
                                                    lowerName.includes("individual") ||
                                                    lowerName.includes("partner")
                                                ) {
                                                    sessionType = "individual";
                                                }

                                                const rescheduleLink = `https://rockethealth.app/reschedule/${session.source_id}?session_type=${sessionType}`;
                                                await navigator.clipboard.writeText(rescheduleLink);
                                                toast({
                                                    title: "Copied",
                                                    description: "Reschedule link copied to clipboard",
                                                });
                                            } else {
                                                toast({
                                                    title: "Error",
                                                    description: "Session source_id not available",
                                                    variant: "destructive",
                                                });
                                            }
                                        } catch (err) {
                                            console.error(err);
                                            toast({
                                                title: "Error",
                                                description: "Failed to copy reschedule link",
                                                variant: "destructive",
                                            });
                                        }
                                    }
                                }
                                disabled={!session.source_id}
                            >
                                Copy Reschedule Link
                            </DropdownMenuItem>
                            {/* Copy stays for view-only; cancelling is a write. */}
                            <HasAccess permission="write">
                            <DropdownMenuItem
                                onClick={() => {
                                    setCancelDialogOpen(true);
                                }}
                            >
                                Cancel Session
                            </DropdownMenuItem>
                            </HasAccess>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Dialog 
                          open={cancelDialogOpen} 
                          onOpenChange={(open) => {
                            setCancelDialogOpen(open);
                            if (!open) {
                              // Reset states when dialog closes
                              setSelectedCancelReason("");
                              setCancelReason(undefined);
                            }
                          }}
                        >
                            <DialogContent>
                                <DialogHeader>
                                <DialogTitle>Cancel Session</DialogTitle>
                                </DialogHeader>
                                <div className="py-4 space-y-4">
                                  <div className="space-y-2">
                                    <label className="text-sm font-medium">
                                      Select cancellation reason
                                    </label>
                                    <Select
                                      value={selectedCancelReason}
                                      onValueChange={setSelectedCancelReason}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select a reason" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {CANCELLATION_REASONS.map((reason) => (
                                          <SelectItem key={reason} value={reason}>
                                            {reason}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {isOthersSelected && (
                                    <div className="space-y-2">
                                      <label className="text-sm font-medium">
                                        Please provide a reason for cancellation
                                      </label>
                                      <Textarea
                                        placeholder="Type your reason here"
                                        value={cancelReason ?? ""}
                                        onChange={(e) =>
                                          setCancelReason(e.target.value || undefined)
                                        }
                                        className="min-h-[100px]"
                                      />
                                    </div>
                                  )}
                                </div>
                                <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                      setCancelDialogOpen(false);
                                      setSelectedCancelReason("");
                                      setCancelReason(undefined);
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => {
                                      const finalReason = isOthersSelected
                                        ? cancelReason
                                        : selectedCancelReason;
                                      
                                      cancelSession({
                                        source_id: session.source_id,
                                        reason: finalReason,
                                      });
                                      setSelectedCancelReason("");
                                      setCancelReason(undefined);
                                      setCancelDialogOpen(false);
                                    }}
                                    disabled={
                                      !selectedCancelReason ||
                                      (isOthersSelected && !cancelReason)
                                    }
                                >
                                    Confirm Cancellation
                                </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                    )}
                </div>
                </div>
            ))}
            </div>
        </div>
        )}

        {/* Users */}
        <div>
        <p className="text-sm font-medium mb-1">Users:</p>
        <div className="space-y-2">
            {users?.map((user) => (
            <div
                key={user.id}
                className="flex justify-between items-center rounded-md border p-2 bg-white"
            >
                <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                    <p className="text-sm font-medium">
                    {onUserClick ? (
                        // Orange rather than the text-blue-600 the rest of the
                        // CRM uses for links: the care team asked for it
                        // explicitly on this screen. Deliberate, so don't
                        // "fix" it to match SessionCard — that surface renders
                        // the same action in blue, and the users table renders
                        // it grey-until-hover, so there was no single
                        // precedent to follow here anyway.
                        <button
                        type="button"
                        onClick={() => onUserClick(user.id)}
                        className="text-left text-orange-500 hover:text-orange-600 hover:underline"
                        >
                        {user.full_name}
                        </button>
                    ) : (
                        user.full_name
                    )}
                    </p>
                    {user.phone_numbers?.[0] && (
                    <p className="text-xs text-muted-foreground">
                        {user.phone_numbers[0]}
                    </p>
                    )}
                    {user.emails?.[0] && (
                    <p className="text-xs text-muted-foreground">
                        {user.emails[0]}
                    </p>
                    )}
                </div>
                </div>
                {resolved_purchaser?.user_id === user.id && (
                <Badge
                    variant="outline"
                    className="text-xs flex items-center gap-1 flex-shrink-0"
                    title={
                    resolved_purchaser.source === "INFERRED"
                        ? "Inferred from payment notes (pack predates payment attribution)"
                        : "Recorded at payment"
                    }
                >
                    <CreditCard className="w-3 h-3" />
                    {resolved_purchaser.source === "INFERRED" ? "Paid*" : "Paid"}
                </Badge>
                )}
            </div>
            ))}

            {/* Partner captured at checkout but not signed up yet (no User row) */}
            {pending_partner && (
            <div className="flex items-center gap-2 rounded-md border border-dashed p-2 bg-muted/30">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                <p className="text-sm font-medium text-muted-foreground">
                    {pending_partner.full_name || "Partner"}{" "}
                    <span className="text-xs font-normal">(not signed up yet)</span>
                </p>
                {pending_partner.phone && (
                    <p className="text-xs text-muted-foreground">{pending_partner.phone}</p>
                )}
                {pending_partner.email && (
                    <p className="text-xs text-muted-foreground">{pending_partner.email}</p>
                )}
                </div>
            </div>
            )}

            {/* If less than 2 users, show input(s) */}
            {/* {users?.map((user) => (
                <div
                key={user.id}
                className="flex justify-between items-center rounded-md border p-2 bg-white"
                >
                <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <div>
                    <p className="text-sm font-medium">{user.full_name}</p>
                    {user.phone_numbers?.[0] && (
                        <p className="text-xs text-muted-foreground">
                        {user.phone_numbers[0]}
                        </p>
                    )}
                    {user.emails?.[0] && (
                        <p className="text-xs text-muted-foreground">
                        {user.emails[0]}
                        </p>
                    )}
                    </div>
                </div>
                </div>
            ))} */}

            {/* If less than 2 users, show input(s) */}
            {users?.length < 2 &&
                newUserIds.map((value, idx) => (
                <div
                    key={`add-user-${idx}`}
                    className="flex items-center gap-2 rounded-md border p-2 bg-white"
                >
                    <Input
                    placeholder={`Enter User ${users?.length + idx + 1} ID`}
                    value={value}
                    onChange={(e) => {
                        const updated = [...newUserIds];
                        updated[idx] = e.target.value;
                        setNewUserIds(updated);
                    }}
                    className="flex-1"
                    />
                    <Button
                    variant="secondary"
                    disabled={!value}
                    onClick={() => {
                        const finalUserIds = [
                        ...users.map((u) => u.id),
                        ...newUserIds.filter((id) => id),
                        ];

                        // Validation: Prevent adding more than 2 users to couples pack
                        if (finalUserIds.length > 2) {
                        toast({
                            title: "Maximum 2 users allowed",
                            description: "Couples therapy pack supports maximum 2 users",
                            variant: "destructive",
                        });
                        return;
                        }

                        handleUpdateUsers(finalUserIds);
                    }}
                    >
                    Add
                    </Button>
                </div>
                ))}
            </div>
        </div>

        {/* Professional section */}
        <div>
          <p className="text-sm font-medium mb-1">Professional:</p>

          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-muted-foreground" />

            <ProfessionalPicker
              professionals={professionals}
              selectedProfessionalId={selectedProfessional}
              onSelect={(id) => handleAssignProfessional(id!)}
              // Reassigning is a write; view-only sees who it is, read-only.
              disabled={!canWrite}
            />
          </div>
        </div>

      </CardContent>

      {/* Footer with actions */}
      <CardFooter className="justify-end">
        {/* COMPLETED packs only offer the document, a write — so no menu for
            view-only there; ACTIVE ones still have the copy links. */}
        {(status === "ACTIVE" || (status === "COMPLETED" && canWrite)) && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {status === "ACTIVE" && (
                  <>
                    <HasAccess permission="read">
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            const scheduleLink = `www.rockethealth.app/schedule/couples-therapy?usage_id=${couplesPack.id}`;
                            await navigator.clipboard.writeText(scheduleLink);
                            toast({ title: "Individual Schedule link copied!" });
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Copy individual session schedule Link
                      </DropdownMenuItem>
                    </HasAccess>
                    <HasAccess permission="read">
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            const scheduleLink = await getIndividialSessionSchedulingLink({couples_pack_usage_identifier: couplesPack.id}); // the nomenclature is not correct here is should be for partner.
                            await navigator.clipboard.writeText(scheduleLink);
                            toast({ title: "Partner Schedule link copied!" });
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Copy Partner session schedule Link
                      </DropdownMenuItem>
                    </HasAccess>
                    <HasAccess permission="read">
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            const scheduleLink = await getJointSessionSchedulingLink({couples_pack_usage_identifier: couplesPack.id});
                            await navigator.clipboard.writeText(scheduleLink);
                            toast({ title: "Schedule link copied!" });
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Copy joint session schedule Link
                      </DropdownMenuItem>
                    </HasAccess>
                  </>
                )}
                <HasAccess permission="write">
                <DropdownMenuItem
                  onClick={() => {
                    toast({ title: "Creating/updating couples therapy document...", description: "Please wait...this may take a few seconds." });
                    createCouplesDocument(couplesPack.id);
                  }}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Create Document
                </DropdownMenuItem>
                </HasAccess>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Cancel Dialog */}
            
          </>
        )}
      </CardFooter>
    </Card>
  );
}
